import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, limit } from 'firebase/firestore';
import { db } from "../firebase/firebaseConfig";
import { CallDoc } from '../types/call';
import { useDeviceRegistry } from './useDeviceRegistry';
import { useActiveFamily } from '../context/ActiveFamilyContext';

/**
 * Hook para escuchar llamadas entrantes en tiempo real
 * Redirige automáticamente a /calls/incoming/:callId cuando hay una llamada
 */
export const useCallListener = () => {
  const navigate = useNavigate();
  const { deviceId, isNative } = useDeviceRegistry();
  const { activeFamily } = useActiveFamily();
  const [incomingCall, setIncomingCall] = useState<CallDoc | null>(null);

  useEffect(() => {
    if (!isNative) return; // Solo para dispositivos nativos
    if (!deviceId) return;
    if (!activeFamily?.id) return;

    // Query para llamadas ringing dirigidas a este dispositivo
    const callsRef = collection(db, 'calls');
    const q = query(
      callsRef,
      where('familyId', '==', activeFamily.id),
      where('status', '==', 'ringing'),
      where('targetDeviceIds', 'array-contains', deviceId),
      limit(10)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const callData = { id: change.doc.id, ...change.doc.data() } as CallDoc;

            // Solo procesar si está ringing
            if (callData.status === 'ringing') {
              setIncomingCall(callData);

              // Navegar a la pantalla de llamada entrante
              navigate(`/calls/incoming/${callData.id}`);
            }
          }
        });
      },
      (error) => {
        console.error('❌ useCallListener snapshot error:', error);
      }
    );

    return () => unsubscribe();
  }, [isNative, deviceId, activeFamily?.id, navigate]);

  return { incomingCall };
};
